import { createAsyncThunk } from "@reduxjs/toolkit";

import logger from "Libs/logger";
import { getProjectId } from "Libs/utils";
import { environmentSelector } from "Reducers/environment";
import { getCommonError } from "Reducers/sliceFactory";

import {
  deploymentSizingApiEnabledSelector,
  loadingNextDeploymentSelector
} from "../deployment.selectors";

import type {
  LoadDeploymentNextProps,
  LoadDeploymentNextPayload
} from "../types";
import type { AsyncThunkOptionType } from "Reducers/types";

export const loadDeploymentNext = createAsyncThunk<
  LoadDeploymentNextPayload,
  LoadDeploymentNextProps,
  AsyncThunkOptionType
>(
  "app/environmentDeployment/next",
  async (
    { organizationId, projectId, environmentId },
    { getState, rejectWithValue }
  ) => {
    try {
      const platformLib = await import("Libs/platform");
      const client = platformLib.default;
      const encodedEnvId = encodeURIComponent(environmentId);

      const projectDescriptionId = getProjectId(getState, projectId)!;
      const deployment = await client.getNextDeployment(
        projectDescriptionId,
        encodedEnvId,
        {
          verbose: true
        }
      );

      const schemaProperties =
        deployment?._links?.self?.meta?.get?.responses?.default?.content?.[
          "application/json"
        ]?.schema?.properties;

      return {
        deployment,
        schemas: schemaProperties
      };
    } catch (error) {
      if (![404, 403].includes((error as { code: number }).code)) {
        const commonError = getCommonError(error).error;
        const errorMessage =
          commonError?.message ??
          "An error occurred while attempting to load deployment.";

        logger(errorMessage, {
          action: "load_deployment_next",
          meta: {
            organizationDescriptionId: organizationId,
            environmentDescriptionId: environmentId,
            projectDescriptionId: projectId
          }
        });
      }

      return rejectWithValue(error as { code: number; message: string });
    }
  },
  {
    condition: ({ organizationId, projectId, environmentId }, { getState }) => {
      const state = getState();
      const isSizingApiEnabled = deploymentSizingApiEnabledSelector(state, {
        organizationId,
        projectId,
        environmentId
      });

      const environment = environmentSelector(state, {
        organizationId,
        projectId,
        environmentId
      });

      const hasNecessaryProps = !!(
        organizationId &&
        projectId &&
        environmentId
      );
      const loadingNextDeployment = loadingNextDeploymentSelector(state);

      if (
        !isSizingApiEnabled ||
        !environment?.has_deployment ||
        !hasNecessaryProps ||
        loadingNextDeployment
      ) {
        return false;
      }
    }
  }
);
